import { Outlet } from 'react-router-dom';
import { Building2 } from 'lucide-react';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { EmptyState } from '../components/EmptyState';
import { useAdvisorFirm } from './useAdvisorFirm';
import { isFirmAdminHere, isFirmAdminWorkspace } from './firmAdmin';
import { useWorkspaceKind } from '../workspace/useWorkspaceKind';
import { AdvisorFirmChooser } from './AdvisorFirmChooser';
import { clearActingFirmId } from './advisorActingFirm';

/**
 * Route guard for the "Your firm" page (steward-app#1361, rewritten steward-app#1742, then
 * again in that issue's review round 2 (R1)). A SIBLING of `RequireAdvisorFirm` in
 * `router.tsx`, not nested under it — see that route's own comment for why: `RequireAdvisorFirm`
 * refuses outright the moment `advisorFor` is null, which is exactly the window (loading,
 * failed, or resolved-to-a-different-firm) this guard needs to stay mounted through in order to
 * render something other than a dead end.
 *
 * ⚠️ **This is UX gating. appcore's authorizer is the server-side boundary**, exactly as the
 * pre-#1742 version of this file said: it mirrors the nav item's visibility in `Layout.tsx` so
 * nobody is linked to a page that refuses them, and it spares everyone else a page of 403s. It
 * is not why the firm's roster, invitations or waiting families are safe — see `firmAdmin.ts`'s
 * header for the full account of what appcore and the façade each authorize.
 *
 * ⚠️ **As of review round 2 (R1), THIS file only decides whether `YourFirm` mounts at all —
 * not what it shows once mounted, for the ordinary case.** Once `workspaceKind === 'firm'`
 * (the active workspace's own packs say so, no membership-read degradation involved), this
 * guard mounts `<Outlet/>` unconditionally the moment the caller is a workspace admin, and
 * `YourFirm` itself (via `isFirmAdminHere` and its own `FirmLookupNotice`) decides whether to
 * show the real firm details, a loading line, `AdvisorFirmChooser`, a mismatch control, or a
 * "not found yet" note — with **People always rendering underneath**, regardless of which.
 * Before this round, this file withheld the WHOLE page (People included) until its OWN firm
 * lookup (`GET /advisor/firms`, a workspace-LESS read answering a DIFFERENT question — see
 * `useAdvisorFirm`'s header) had settled and matched, which combined with Settings > Members
 * being hidden the instant a workspace is known to be a firm's (`isKnownFamilyWorkspace`,
 * independent of this same lookup) to strand an admin with no reachable surface at all during a
 * mismatch, a failed lookup, or a genuine "no firm at all" answer. The states below, and the
 * five-state sequence that follows this note, are UNCHANGED and still exactly what renders for
 * the one case this round does not touch: a DEGRADED membership read (`packsResolved === false`)
 * whose firm lookup has not yet told `isFirmAdminWorkspace` whether to admit the caller at all —
 * `workspaceKind` cannot be trusted as `'firm'` in that quadrant (`useWorkspaceKind.ts`'s own
 * header), so there is no "mount unconditionally" shortcut available for it, and the old
 * sequential gating remains the only correct answer there.
 *
 * Five states, in order — for the degraded-and-still-unsettled case only, since the branch just
 * above returns before reaching this sequence for every ordinary firm-pack workspace:
 *
 *   0. **`activeMembership` itself unknown** (`/me` still resolving): render nothing.
 *   1. **Not reachable at all** (`!isFirmAdminWorkspace`, membership now known): not a
 *      workspace admin, or not a firm workspace by pack (or, during a degraded membership read
 *      whose firm lookup has already SETTLED, the #700 id-comparison stand-in `isFirmWorkspace`
 *      carries). A plain refusal — same copy as before #1742, since this case is unchanged by
 *      it. **Not** the case for a degraded read whose lookup is still `isLoading` or has
 *      `isError`'d — `isFirmAdminWorkspace` admits that quadrant outright (security review,
 *      2026-09-23: see its own header) so it falls through to state 2's "render nothing" while
 *      loading, or state 4's plain retry once a still-degraded read's lookup settles with an
 *      error, instead of refusing someone who may yet turn out to run a firm.
 *   2. **The firm lookup has not settled yet** (`isLoading`): render nothing, the same
 *      "decide nothing yet" rule every gate in this codebase follows for this window
 *      (`RequireAdmin`, the pre-#1742 version of this file). Convention here, not a missing
 *      spinner — see `useWorkspaceKind.ts`'s own callers for the same choice.
 *   3. **The façade could not tell which firm this caller means** (`firmChoice`): the existing
 *      `AdvisorFirmChooser`, unchanged.
 *   4. **Settled.** `isFirmAdminHere` (this workspace's id matches `advisorFor.firmWorkspaceId`)
 *      renders the real page. Otherwise: an admin at TWO firms whose acting choice currently
 *      names a different one (`advisorFor` truthy but mismatched) gets a "choose this
 *      workspace's firm" control that clears the acting-firm choice and re-asks; anything else
 *      (a failed lookup, or a settled answer naming no firm at all) gets a plain "try again"
 *      that refetches.
 *
 * **Case 4's mismatch branch is a manual re-choose, not an automatic follow — a deliberate,
 * reported deviation from #1742's literal ask that the Firm page "follow the active
 * workspace."** `GET /advisor/firms` is workspace-LESS by design (`useAdvisorFirm`'s own
 * header) and its `firmChoice` payload deliberately omits `firmWorkspaceId`
 * (`services/steward-app-api/src/handlers/advisor.ts`'s `firmChoicePayload`, "the payload
 * never hands out an id for a firm this request is not acting as") — so this UNNARROWED
 * response alone cannot tell which candidate matches the active workspace.
 *
 * ⚠️ **Correction (steward-app#1742 review round 2, R3): an earlier version of this comment
 * claimed there is "no façade-free way" to resolve that at all. That was wrong.**
 * `?firmId=<id>` NARROWS `advisorFirmContext` to that one candidate
 * (`services/steward-app-api/src/handlers/advisor.ts`'s own header — the field may only
 * narrow, never widen), and the settled response for a NARROWED request does carry
 * `firmWorkspaceId`. So a per-candidate probe — call `GET /advisor/firms?firmId=<id>` once for
 * each entry in `firmChoice` and compare the returned `firmWorkspaceId` to `activeWorkspaceId`
 * — is possible today, entirely through the façade. Nobody has built it: it is N requests
 * instead of one, R1 (this same review round) already makes People reachable without it, and
 * an admin genuinely running two firms in the same tab still has to choose consciously when
 * neither candidate matches the workspace they just switched to. Recorded here as a possible
 * later improvement, not built in this round.
 *
 * Clearing the acting-firm choice and re-showing `AdvisorFirmChooser` asks the admin to
 * confirm instead, which never crosses a firm boundary without an explicit click, and keeps
 * `firmAdmin.ts`'s mixed-scoping hazard closed: this branch renders NEITHER firm's data, only
 * a control to fix the mismatch.
 *
 * Not a new pattern: `RequireAdvisorFirm.tsx`'s own "Change firm" button is the same
 * `clearActingFirmId()`-then-let-the-chooser-reappear mechanism, already shipped and tested
 * there (steward-app#983 D4). This mirrors it rather than adding a refetch of its own — clearing
 * the acting firm changes `useAdvisorFirm`'s query KEY (`advisorFirmsQueryOptions`), and the
 * next render picks that up on its own; calling `refetch()` here too would race the still-stale
 * key from this same render.
 */
export function RequireFirmAdmin() {
  const { activeMembership, activeWorkspaceId } = useWorkspace();
  const workspaceKind = useWorkspaceKind();
  const { advisorFor, firmChoice, isLoading, isError, refetch } = useAdvisorFirm();

  // Render nothing while `/me` is resolving, same as before #1742 and the same rule
  // `RequireAdmin` follows — we cannot yet tell whether this caller is even a workspace admin,
  // so there is nothing to refuse OR admit yet. `isFirmAdminWorkspace` itself also reads
  // `false` for a null membership, but returning its refusal EmptyState directly here would
  // flash that refusal before a real admin's membership has even loaded.
  if (!activeMembership) return null;

  if (!isFirmAdminWorkspace(activeMembership, workspaceKind, activeWorkspaceId, advisorFor, isLoading || isError)) {
    return (
      <EmptyState
        icon={Building2}
        data-testid="your-firm-refused"
        message="This page is for your firm’s admins, from inside the firm’s own workspace. If you run the firm, switch to its workspace. Otherwise, one of its admins can help."
      />
    );
  }

  // The ordinary case (steward-app#1742 review round 2, R1): the active workspace's OWN packs
  // already say it is a firm's, with no membership-read degradation in play, so nothing about
  // `YourFirm`'s reachability needs to wait on the SEPARATE firm lookup below —
  // `isFirmAdminWorkspace` above already confirmed this caller is a workspace admin, and for
  // `workspaceKind === 'firm'` its own fallback branch reduces to exactly that plus this check,
  // so this adds no new admission. `YourFirm` decides for itself, via `isFirmAdminHere` and its
  // own `FirmLookupNotice`, what to show above People while this lookup is loading, mismatched,
  // errored, or settled with no firm at all — see this file's own header. The remaining case
  // (degraded packs, admitted above only because the lookup is ALSO still unsettled) leaves
  // `workspaceKind` unable to answer `'firm'` at all (`useWorkspaceKind.ts`'s stand-in), so it
  // falls through to the untouched sequence below.
  if (workspaceKind === 'firm') return <Outlet />;

  if (isLoading) return null;

  if (firmChoice) {
    return <AdvisorFirmChooser firms={firmChoice} />;
  }

  if (isFirmAdminHere(activeMembership, activeWorkspaceId, advisorFor)) {
    return <Outlet />;
  }

  const mismatched = !!advisorFor && advisorFor.firmWorkspaceId !== activeWorkspaceId;
  return (
    <EmptyState
      icon={Building2}
      data-testid="your-firm-unsettled"
      message={
        mismatched
          ? 'You are working as a different firm right now. Choose the firm for this workspace to see its details.'
          : 'We could not load your firm just now. Try again in a moment.'
      }
      action={{
        label: mismatched ? 'Choose this workspace’s firm' : 'Try again',
        onAction: mismatched ? clearActingFirmId : () => { void refetch(); },
      }}
    />
  );
}
